import { FC, useState } from "react";
import { createPortal } from "react-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useData } from "data/data-provider";
import {
  formatPrice,
  getEnabledPeriodPrices,
  splitPeriodRows,
  cardPhotoUrl,
  dailyRate,
  markupPercent,
  moreRatesLabel,
} from "@1-now/booking-core";
import toast from "react-hot-toast";
import RequestPriceButton from "shared/RequestPriceButton/RequestPriceButton";
import { specNumber, specText } from "utils/carSpecs";

export interface FleetCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  simpleImg?: boolean;
  onClick?: any;
  clickFunction?: any;
  loading?: boolean;
  /**
   * The car has no price and is not the booking's own car: it can't be
   * swapped to, so the card asks for a price instead of taking a click.
   */
  priceOnRequest?: boolean;
}

const FleetCard: FC<FleetCardProps> = ({
  size = "default",
  className = "",
  data,
  simpleImg = false,
  onClick,
  clickFunction,
  loading,
  priceOnRequest = false,
}) => {
  const { setSelectedCar, selectedCar, ourCompany } = useData();
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const {
    id,
    name,
    color,
    description,
    seats,
    increased_price,
  } = data!;
  const seatsNum = specNumber(seats);
  const colorText = specText(color);

  const increasedPriceNum = Number(increased_price);
  // The daily rate and the markup of the crossed-out price over it, by the
  // rule every design's card follows (dailyRate / markupPercent in
  // @1-now/booking-core): the trip average, else the list price. A car priced
  // only on `price` used to show no rate here. A car with no base price
  // shows no amount at all, so it carries no markup either.
  const currentPriceNum = dailyRate(data) ?? 0;
  const markup = priceOnRequest ? null : markupPercent(data);
  const hasDiscount = markup != null;
  const discountPercent = markup ?? 0;

  const periodPrices = getEnabledPeriodPrices(
    currentPriceNum,
    ourCompany,
    data?.period_prices,
  );
  const primaryPeriod = periodPrices[0];
  const [tiersExpanded, setTiersExpanded] = useState(false);
  const { shown: secondaryPeriods, hiddenCount } = splitPeriodRows(
    periodPrices.slice(1),
    tiersExpanded,
  );

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const handleMouseLeave = () => setIsHovered(false);

  return (
    <div
      className={`nc-StayCard group relative  rounded-md border ${
        selectedCar && selectedCar.id === id
          ? "border-primary-brand"
          : "border-secondary-100 dark:border-neutral-800"
      } rounded-2xl overflow-hidden hover:shadow-xl transition-shadow ${
        priceOnRequest ? "cursor-default" : ""
      } ${className}`}
      data-nc-id="StayCard"
      onClick={priceOnRequest ? undefined : onClick}
    >
      {selectedCar && selectedCar.id === id && (
        <div className="absolute top-3 right-3 z-10 bg-primary-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          Selected
        </div>
      )}
      <div className="p-4">
        <div className="relative rounded-none overflow-hidden">
          <img
            src={
            cardPhotoUrl(data) ?? "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
          }
            alt={name}
            className="h-[200px] object-cover w-full"
          />
        </div>
      </div>

      {/* Fleet Info */}
      <div className={size === "default" ? "p-4 space-y-4" : "p-3 space-y-2"}>
        <div className="flex items-center justify-between gap-2">
          <h2
            className={`font-medium capitalize min-w-0 flex-1 ${
              size === "default" ? "text-lg" : "text-base"
            }`}
          >
            <p className="truncate w-full max-w-full">{name}</p>
          </h2>
          {hasDiscount && (
            <span className="shrink-0 text-sm font-bold tracking-wide text-button-color">
              {discountPercent}% OFF
            </span>
          )}
        </div>

        {/* Description with Tooltip */}
        <div className="relative group">
          <span
            className="block line-clamp-2 truncate cursor-pointer relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {description}
          </span>
      </div>

        {/* Tooltip (Using React Portal to prevent clipping) */}
      {isHovered &&
        createPortal(
          <div
              className="absolute bg-[#fbfaeb] text-black text-xs rounded-md px-2 py-1 shadow-lg z-50"
            style={{
              top: tooltipPosition.top + 24,
              left: tooltipPosition.left,
              maxWidth: "250px",
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {description}
          </div>,
          document.body,
        )}

        {/* Fleet Details: a spec with no value is not shown (1NOW-11141) */}
        {(seatsNum || colorText) && (
          <span className="text-sm">
            {seatsNum && (
              <>
                <span className="font-semibold">{seatsNum}</span> seats{" "}
              </>
            )}
            {colorText && (
              <>
                <span className="font-semibold">{colorText}</span> color
              </>
            )}
          </span>
        )}

        {/* Pricing and Select Button */}
        <div className="w-14 border-b border-neutral-100 dark:border-neutral-800"></div>
        {priceOnRequest && (
          <div className="flex flex-col items-start gap-2">
            <span className="text-base font-semibold leading-tight">
              Price on request
            </span>
            <RequestPriceButton
              car={data}
              className="nc-Button inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors bg-primary-6000 hover:bg-primary-700 text-on-primary"
            />
          </div>
        )}
        {!priceOnRequest && (
          <div className="flex flex-col">
            {hasDiscount && primaryPeriod.kind === "day" && (
              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400 line-through leading-tight">
                {"$" + formatPrice(increasedPriceNum)}
              </span>
            )}
            <span className="text-base font-semibold leading-tight">
              {primaryPeriod.formatted}
              {size === "default" && (
                <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                  {" "}
                  {primaryPeriod.label}
                </span>
              )}
            </span>
            {size === "default" && secondaryPeriods.length > 0 && (
              <>
                <div className="mt-1.5 pt-1.5 border-t border-neutral-100 dark:border-neutral-800 grid grid-cols-[auto_auto] justify-start gap-x-2 leading-tight">
                  {secondaryPeriods.map((p) => (
                    <div key={p.days} className="contents">
                      <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        {p.formatted}
                      </span>
                      <span className="self-center text-xs font-normal text-neutral-500 dark:text-neutral-400">
                        {p.label}
                      </span>
                    </div>
                  ))}
                </div>
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    // The whole card is clickable, so expanding in place
                    // has to stop the click reaching it.
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setTiersExpanded(true);
                    }}
                    className="mt-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 underline underline-offset-2"
                  >
                    {moreRatesLabel(hiddenCount)}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FleetCard;
